import { businessSection, replySection } from "modules/ebiForms/common";
import { EbiCondition, IEbiField, IEbiFormSection } from "modules/ebiForms/typings";

// Bloki wspólne pakietu „planowanie, nieruchomości i podatki lokalne” (A1, A3, A5–A8): oświadczenie o korespondencji
// elektronicznej z formularzy upzp, działki wskazywane identyfikatorem i podatnik niebędący osobą fizyczną.

const REPLY_IN_EBI: EbiCondition = { field: "sposobOdbioru", equals: "ebi" };

const ELECTRONIC_DELIVERY =
  "na doręczanie korespondencji w niniejszej sprawie za pomocą środków komunikacji elektronicznej w rozumieniu art. 2 pkt 5 ustawy z dnia 18 lipca 2002 r. o świadczeniu usług drogą elektroniczną";

/**
 * Sposób odbioru odpowiedzi + „oświadczenie w sprawie korespondencji elektronicznej” z obligatoryjnych formularzy upzp
 * (wniosek WZ — pkt 9, pismo dotyczące aktu planowania — pkt 8). Odbiór w EBI wymaga zgody; przy odbiorze pocztą lub
 * osobiście petent odpowiada jak we wzorze (domyślnie „nie wyrażam zgody”).
 */
export const electronicDeliverySection = (title: string): IEbiFormSection => {
  const reply = replySection(title);

  return {
    ...reply,
    fields: [
      ...reply.fields,
      {
        code: "zgodaKorespondencjaEbi",
        type: "declaration",
        label: `Wyrażam zgodę ${ELECTRONIC_DELIVERY}.`,
        required: true,
        visibleWhen: REPLY_IN_EBI,
      },
      {
        code: "zgodaKorespondencja",
        type: "radio",
        label: "Oświadczenie w sprawie korespondencji elektronicznej",
        required: true,
        defaultValue: "brakZgody",
        visibleWhen: { not: REPLY_IN_EBI },
        options: [
          { value: "zgoda", label: `Wyrażam zgodę ${ELECTRONIC_DELIVERY}` },
          { value: "brakZgody", label: `Nie wyrażam zgody ${ELECTRONIC_DELIVERY}` },
        ],
      },
    ],
  };
};

/**
 * Działki wskazywane identyfikatorem z ewidencji gruntów — w formularzach upzp w postaci elektronicznej „podaje się
 * wyłącznie identyfikator działki ewidencyjnej lub działek ewidencyjnych” (przypis 6 wzoru WZ).
 */
export const parcelIdsField = (code: string, label: string, hint?: string): IEbiField => ({
  code,
  type: "repeat",
  label,
  hint,
  itemLabel: "Działka",
  required: true,
  min: 1,
  max: 50,
  fields: [
    {
      code: "identyfikator",
      type: "text",
      label: "Identyfikator działki ewidencyjnej",
      required: true,
      maxLength: 60,
      placeholder: "np. 141201_1.0001.125/4",
      hint: "Kod jednostki ewidencyjnej, numer obrębu i numer działki — np. z serwisu Geoportal albo z wypisu z ewidencji gruntów.",
    },
  ],
});

/**
 * Podatnik lub właściciel nieruchomości niebędący osobą fizyczną (osoba prawna, jednostka organizacyjna, w tym spółka
 * nieposiadająca osobowości prawnej) — pola przedsiębiorcy z bloku wspólnego, nazwane jak w formularzach podatkowych.
 */
export const entitySection = (title: string, visibleWhen: EbiCondition): IEbiFormSection => {
  const section = businessSection(title, visibleWhen);
  const fields = section.fields.flatMap((field): IEbiField[] => {
    if (field.code === "firmaNazwa") {
      return [
        { ...field, label: "Nazwa pełna" },
        { code: "firmaNazwaSkrocona", type: "text", label: "Nazwa skrócona", maxLength: 200 },
      ];
    }
    if (field.code === "firmaKrs") return [{ ...field, label: "Numer KRS (jeśli podmiot jest wpisany do KRS)" }];
    if (field.code === "firmaReprezentacja") return [{ ...field, label: "Twoja podstawa reprezentacji (np. członek zarządu, zarządca, pełnomocnik)" }];
    return [field];
  });

  return { ...section, code: "podmiot", fields };
};
